import { Combobox } from "@msuite/ui/form";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { ProtocolFormSchema } from "@msuite/utils/schemas";
import { getFullName } from "@msuite/utils/users";
import React from "react";
import { useFormContext } from "react-hook-form";
import { ContactMemberCard } from "@/components/cards/contact-member-card";
import { client } from "@/hono";
import { AddContactMemberModal } from "@/modals/contacts/add-contact-member-modal";

export function ProtocolFormTypeCallRequestCalledBy() {
	/** Context */
	const methods = useFormContext<ProtocolFormSchema>();

	/** Hooks */
	const addContactMember = useDisclosure();
	const { t } = useTranslation();

	/** Query */
	const { data: contactMembers, refetch } = useQuery({
		queryKey: ["contact-members"],
		queryFn: async () => {
			const response = await client.contacts.members.$get();
			const data = await responseHandler(response);
			return data.contactMembers;
		},
	});

	/** Render */
	return (
		<React.Fragment>
			<Combobox
				control={methods.control}
				displayValue={({ item }) => getFullName(item)}
				renderOption={({ item }) => {
					/** Render */
					return (
						<div className="v-stack gap-0">
							<p className="label">{item?._contact?.name}</p>
							<ContactMemberCard
								contactMember={item}
								variant="plain"
							/>
						</div>
					);
				}}
				placeholder={t("general.select")}
				label={t("protocols.caller")}
				isRequired
				middleware={() => {
					methods.setValue("callRequestDetails.callRequestByIdSendNotification", true);
				}}
				keyExtractor={(item) => item.item.id}
				name="callRequestDetails.callRequestById"
				options={contactMembers?.map((member) => ({
					label: getFullName(member),
					value: member.id,
					item: { ...member },
				}))}
				buttons={[
					{
						tags: ["add", "add contact"],
						onClick: () => {
							addContactMember.onOpen();
						},
						render: () => (
							<div className="h-stack">
								<Icon name="Plus" />
								<p>{t("contacts.add_contact")}</p>
							</div>
						),
					},
				]}
			/>
			<AddContactMemberModal
				isOpen={addContactMember.isOpen}
				onClose={addContactMember.onClose}
				onComplete={async (contact) => {
					await refetch();
					if (contact?.id) {
						methods.setValue("callRequestDetails.callRequestById", contact.id);
					}
				}}
				contactId={null}
			/>
		</React.Fragment>
	);
}
